/**
 * FilterBar Component
 * Compact search, category tabs, and filter dropdowns in single row
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  FaSearch, 
  FaPlus, 
  FaCog, 
  FaFilter, 
  FaTimes
} from 'react-icons/fa';
import styles from './FilterBar.module.css';

const FilterBar = ({
  categories = [],
  selectedCategory,
  onCategoryChange,
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
  programCounts = {},
  canManage = false,
  onAddProgram,
  onManageCategories,
  showFilters,
  onToggleFilters
}) => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const filterPanelRef = useRef(null);

  // Close filter panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
        setIsFilterPanelOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  // Check if any filters are active
  const hasActiveFilters = 
    filters.age || 
    filters.sport || 
    filters.gender || 
    filters.availability !== 'all';

  // Clear all filters
  const clearFilters = () => {
    onFiltersChange({
      age: '',
      sport: '',
      gender: '',
      availability: 'all'
    });
    onSearchChange('');
    onCategoryChange('all');
  };

  return (
    <>
      <div className={styles.filterBar}>
        <div className={styles.filterBarContent}>
          {/* Single Row: Categories (Tabs) + Search + Filter + Settings */}
          <div className={styles.mainRow}>
            {/* Category Tabs */}
            <div className={styles.categoryTabs}>
              {categories.map(category => {
                const count = programCounts[category._id] || 0;
                const isActive = selectedCategory === category._id;
                
                return (
                  <button
                    key={category._id}
                    className={`${styles.categoryTab} ${isActive ? styles.active : ''}`}
                    onClick={() => onCategoryChange(category._id)}
                    disabled={count === 0 && category._id !== 'all'}
                  >
                    <span className={styles.tabName}>{category.name}</span>
                    <span className={styles.tabCount}>{count}</span>
                  </button>
                );
              })}
            </div>

            {/* Right Side: Search + Filter + Settings */}
            <div className={styles.rightControls}>
              {/* Compact Search */}
              <div className={styles.searchWrapper}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className={styles.searchInput}
                />
                {searchQuery && (
                  <button 
                    className={styles.clearSearch}
                    onClick={() => onSearchChange('')}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>

              {/* Filter Toggle */}
              <button 
                className={`${styles.iconBtn} ${isFilterPanelOpen ? styles.active : ''} ${hasActiveFilters ? styles.hasFilters : ''}`}
                onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                title="Filters"
              >
                <FaFilter />
                {hasActiveFilters && <span className={styles.filterBadge} />}
              </button>

              {/* Settings (Categories) - Only for managers */}
              {canManage && (
                <button 
                  className={styles.iconBtn}
                  onClick={onManageCategories}
                  title="Manage Categories"
                >
                  <FaCog />
                </button>
              )}

              {/* Clear All (if filters active) */}
              {hasActiveFilters && (
                <button 
                  className={styles.clearAllBtn}
                  onClick={clearFilters}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Filter Panel (Dropdown) */}
          {isFilterPanelOpen && (
            <div className={styles.filterPanel} ref={filterPanelRef}>
              <div className={styles.filterPanelHeader}>
                <h3>Filters</h3>
                <button 
                  className={styles.closePanel}
                  onClick={() => setIsFilterPanelOpen(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <div className={styles.filterGrid}>
                {/* Age Filter */}
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Age</label>
                  <input
                    type="number"
                    placeholder="Enter age"
                    value={filters.age}
                    onChange={(e) => handleFilterChange('age', e.target.value)}
                    className={styles.filterInput}
                    min="1"
                    max="99"
                  />
                </div>

                {/* Sport Filter */}
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Sport</label>
                  <select
                    value={filters.sport}
                    onChange={(e) => handleFilterChange('sport', e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="">All Sports</option>
                    <option value="Soccer">Soccer</option>
                    <option value="Basketball">Basketball</option>
                    <option value="Tennis">Tennis</option>
                    <option value="Volleyball">Volleyball</option>
                    <option value="Baseball">Baseball</option>
                    <option value="Swimming">Swimming</option>
                  </select>
                </div>

                {/* Gender Filter */}
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Gender</label>
                  <select
                    value={filters.gender}
                    onChange={(e) => handleFilterChange('gender', e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="">All</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Co-ed">Co-ed</option>
                  </select>
                </div>

                {/* Availability Filter */}
                <div className={styles.filterGroup}>
                  <label className={styles.filterLabel}>Availability</label>
                  <select
                    value={filters.availability}
                    onChange={(e) => handleFilterChange('availability', e.target.value)}
                    className={styles.filterSelect}
                  >
                    <option value="all">All Programs</option>
                    <option value="available">Available</option>
                    <option value="full">Full (Waitlist)</option>
                  </select>
                </div>
              </div>

              <div className={styles.filterPanelFooter}>
                <button 
                  className={styles.applyFilters}
                  onClick={() => setIsFilterPanelOpen(false)}
                >
                  Apply Filters
                </button>
                {hasActiveFilters && (
                  <button 
                    className={styles.resetFilters}
                    onClick={() => {
                      handleFilterChange('age', '');
                      handleFilterChange('sport', '');
                      handleFilterChange('gender', '');
                      handleFilterChange('availability', 'all');
                    }}
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Add Program Button */}
      {canManage && (
        <button 
          className={styles.floatingAddBtn}
          onClick={onAddProgram}
          title="Add Program"
        >
          <FaPlus />
          <span className={styles.floatingBtnText}>Add Program</span>
        </button>
      )}
    </>
  );
};

export default FilterBar;










